import * as Sentry from '@sentry/react';
import { useState } from 'react';

import { random } from 'lodash';

import { EntertainerFeaturedCta, useCurrentEntertainerQuery } from '@queries/EntertainerQueries';

import { useUpdateEntertainerMutation } from '@mutations/EntertainerMutations';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { FormLabel } from '@components/form/FormLabel';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { Popup } from '@components/layout/Popup/Popup';
import { PrimaryCtaCard } from '@components/layout/PrimaryCtaCard/PrimaryCtaCard';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import brokenImageUrl from './assets/broken-image.svg';

interface AddEntertainerPrimaryCtaLinkPopupContentProps {
  onClose: () => void;
}

const AddEntertainerPrimaryCtaLinkPopupContent = ({
  onClose,
}: AddEntertainerPrimaryCtaLinkPopupContentProps) => {
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const { isSubmitting, updateEntertainerAsync } = useUpdateEntertainerMutation();

  const [featuredCtas, setFeaturedCtas] = useState<(EntertainerFeaturedCta & { key: string })[]>([
    {
      key: random(999999999).toString(),
      img: '',
      title: '',
      description: '',
      url: '',
    },
  ]);

  const handleAddFeaturedCta = () => {
    setFeaturedCtas((prevFeaturedCtas) => [
      ...prevFeaturedCtas,
      { key: random(999999999).toString(), img: '', title: '', description: '', url: '' },
    ]);
  };

  const handleSubmit = async () => {
    try {
      const updatedFeaturedCtas = [
        ...(currentEntertainer?.featuredCtas ?? []),
        ...featuredCtas.map(({ key, ...featuredCta }) => featuredCta),
      ];

      await updateEntertainerAsync({ featuredCtas: updatedFeaturedCtas });

      onClose();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
  };

  const canSubmit = featuredCtas.every(
    (featuredCta) => featuredCta.img && featuredCta.title && featuredCta.url,
  );

  return (
    <div className="d-grid gap-2">
      <TitleToolbar text="Add featured links" size="md" className="mb-2" />
      <div style={{ fontSize: 13, opacity: 0.8 }}>Preview</div>
      <div className="d-grid gap-2 mb-4">
        {featuredCtas.map((featuredCta) => (
          <PrimaryCtaCard
            key={`preview-${featuredCta.key}`}
            cta={{
              img: featuredCta.img || brokenImageUrl,
              title: featuredCta.title || 'Link title',
              description: featuredCta.description || 'Link description',
              url: featuredCta.url,
            }}
            track={{
              event: undefined,
            }}
          />
        ))}
      </div>
      <div className="d-grid gap-2">
        {featuredCtas.map((featuredCta, index) => (
          <div
            key={`form-section-${featuredCta.key}`}
            style={{
              background: 'rgba(255,255,255,.1)',
              padding: 16,
              borderRadius: 8,
            }}
          >
            <div className="d-flex gap-2 align-items-center">
              <div className="d-grid gap-2">
                <div className="d-flex gap-2 align-items-center w-100">
                  <div
                    style={{
                      width: 100,
                      height: '100%',
                    }}
                  >
                    <ImageUploadField
                      py="py-3"
                      placeholder={
                        <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Add image *</span>
                      }
                      style={{ border: '1px solid #999', borderRadius: 4 }}
                      imgStyle={{ width: '100%', height: '100%' }}
                      defaultValue={featuredCta.img}
                      onChange={(updatedEventImage) => {
                        setFeaturedCtas((prevFeaturedCtas) => {
                          const updatedFeaturedCtas = [...prevFeaturedCtas];
                          updatedFeaturedCtas[index].img = updatedEventImage.toString();
                          return updatedFeaturedCtas;
                        });
                      }}
                    />
                  </div>
                  <div className="d-grid gap-2">
                    <div>
                      <Input
                        variant="outlined"
                        type="text"
                        placeholder="Eye makeup tutorial, etc. *"
                        defaultValue={featuredCta.title}
                        onChange={(e) => {
                          setFeaturedCtas((prevFeaturedCtas) => {
                            const updatedFeaturedCtas = [...prevFeaturedCtas];
                            updatedFeaturedCtas[index].title = e.target.value;
                            return updatedFeaturedCtas;
                          });
                        }}
                      />
                    </div>
                    <div>
                      <Input
                        variant="outlined"
                        type="text"
                        placeholder="Watch how I do it"
                        defaultValue={featuredCta.description}
                        onChange={(e) => {
                          setFeaturedCtas((prevFeaturedCtas) => {
                            const updatedFeaturedCtas = [...prevFeaturedCtas];
                            updatedFeaturedCtas[index].description = e.target.value;
                            return updatedFeaturedCtas;
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="w-100">
                  <div className="d-flex gap-2 align-items-center">
                    <div className="w-100">
                      <Input
                        variant="outlined"
                        type="text"
                        placeholder="https://www.example.com *"
                        defaultValue={featuredCta.url}
                        onChange={(e) => {
                          setFeaturedCtas((prevFeaturedCtas) => {
                            const updatedFeaturedCtas = [...prevFeaturedCtas];
                            updatedFeaturedCtas[index].url = e.target.value;
                            return updatedFeaturedCtas;
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="shrink-0">
                <IconButton
                  variant="smooth"
                  color="danger"
                  disabled={index === 0}
                  onClick={() => {
                    setFeaturedCtas((prevFeaturedCtas) => {
                      const updatedFeaturedCtas = [...prevFeaturedCtas].filter(
                        (_, i) => i !== index,
                      );
                      return updatedFeaturedCtas;
                    });
                  }}
                >
                  <FontAwesomeIcon name="times" />
                </IconButton>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div>
        <Button variant="flat" color="neutral" roundness="rounded" onClick={handleAddFeaturedCta}>
          Add another link
        </Button>
      </div>

      <div className="mt-2">
        <Button roundness="rounded" disabled={!canSubmit || isSubmitting} onClick={handleSubmit}>
          {featuredCtas.length > 1 ? 'Add links' : 'Add link'}
        </Button>
      </div>
    </div>
  );
};

interface AddEntertainerPrimaryCtaLinkPopupProps extends Omit<
  AddEntertainerPrimaryCtaLinkPopupContentProps,
  'currentEntertainer'
> {
  isVisible: boolean;
}

export const AddEntertainerPrimaryCtaLinkPopup = ({
  isVisible,
  onClose,
}: AddEntertainerPrimaryCtaLinkPopupProps) => {
  return (
    <Popup isVisible={isVisible} onClose={onClose} height="full">
      {isVisible && (
        <Popup.Content>
          <AddEntertainerPrimaryCtaLinkPopupContent onClose={onClose} />
        </Popup.Content>
      )}
    </Popup>
  );
};
